<script setup>
console.log('🚀 GM Conversation Trend Chart: Initializing...');

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, registerables } from 'chart.js';
import GmChartControls from '../filters/GmChartControls.vue';
import GmAnalyticsAPI from 'dashboard/api/gmAnalytics';

Chart.register(...registerables);

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Conversation Volume Trend'
  },
  height: {
    type: Number,
    default: 300
  },
  channels: {
    type: Array,
    default: () => []
  },
  agents: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['refresh', 'export', 'filters-change']);

const { t } = useI18n();

console.log('📊 GM Conversation Trend Chart: Props received:', {
  data: props.data,
  title: props.title
});

// Reactive data
const isLoading = ref(false);
const lastUpdated = ref(null);
const filters = ref({
  dateRange: { from: null, to: null, preset: 'last_30_days' },
  period: { type: 'month', value: null },
  channel: null,
  agent: null
});
const chartInstance = ref(null);
const chartCanvas = ref(null);
const filteredData = ref([]);

// Chart configuration
const chartConfig = ref({
  type: 'line',
  data: {
    labels: [],
    datasets: [{
      label: 'Conversations',
      data: [],
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#3b82f6',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#3b82f6',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            return `${context.parsed.y} conversations`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6',
          drawBorder: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          },
          callback: function(value) {
            return value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  }
});

// Process data for chart
const processChartData = (rawData) => {
  if (!rawData || rawData.length === 0) {
    return {
      labels: [],
      data: []
    };
  }
  
  // Process real data
  const labels = rawData.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  
  const data = rawData.map(item => item.count);
  
  return { labels, data };
};

// Computed properties
const chartData = computed(() => {
  const processed = processChartData(props.data);
  return {
    ...chartConfig.value,
    data: {
      ...chartConfig.value.data,
      labels: processed.labels,
      datasets: [{
        ...chartConfig.value.data.datasets[0],
        data: processed.data
      }]
    }
  };
});

// Chart statistics
const chartStats = computed(() => {
  if (!props.data || props.data.length === 0) return null;
  
  const values = props.data.map(item => item.count);
  const total = values.reduce((sum, val) => sum + val, 0);
  const average = total / values.length;
  const max = Math.max(...values);
  const min = Math.min(...values);
  
  return {
    total,
    average: Math.round(average),
    max,
    min,
    trend: values.length > 1 ? (values[values.length - 1] - values[0]) : 0
  };
});

// Methods
const updateChart = async () => {
  console.log('🔄 GM Conversation Trend Chart: Updating chart with new data...');
  
  if (!chartCanvas.value) return;
  
  // Destroy existing chart if it exists
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
  
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  
  if (!dataToUse || dataToUse.length === 0) {
    console.log('📊 GM Conversation Trend Chart: No data to display');
    return;
  }
  
  const labels = dataToUse.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  
  const data = dataToUse.map(item => item.count || 0);
  
  const ctx = chartCanvas.value.getContext('2d');
  
  chartInstance.value = new Chart(ctx, {
    type: 'line',
    data: {
      labels: labels,
      datasets: [{
        label: 'Conversations',
        data: data,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#10b981',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              return `Conversations: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#f3f4f6',
            drawBorder: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            }
          }
        }
      }
    }
  });
  
  console.log('✅ GM Conversation Trend Chart: Chart updated successfully');
};

const fetchChartData = async () => {
  console.log('🔄 GM Conversation Trend Chart: Fetching chart data with filters:', filters.value);
  try {
    isLoading.value = true;
    
    // Make API call to get filtered conversation trend data
    const response = await GmAnalyticsAPI.getConversationTrend(filters.value);
    console.log('🔍 GM Conversation Trend Chart: API response:', response);
    
    if (response.data && response.data.success) {
      // Update the chart data with filtered results
      filteredData.value = response.data.data.conversationTrend || [];
      console.log('✅ GM Conversation Trend Chart: Filtered data received:', filteredData.value);
    }
    
    lastUpdated.value = new Date().toISOString();
    console.log('✅ GM Conversation Trend Chart: Chart data fetched successfully');
    
    // Update the chart with new data
    await updateChart();
  } catch (error) {
    console.error('❌ GM Conversation Trend Chart: Error fetching chart data:', error);
    // Fallback to original data if API fails
    filteredData.value = props.data || [];
    await updateChart();
  } finally {
    isLoading.value = false;
  }
};

const refreshChart = () => {
  console.log('🔄 GM Conversation Trend Chart: Refreshing chart...');
  isLoading.value = true;
  
  // Emit refresh event to parent
  emit('refresh', { chartType: 'conversation-trend', filters: filters.value });
  
  // Simulate loading
  setTimeout(() => {
    isLoading.value = false;
    lastUpdated.value = new Date().toISOString();
    updateChart();
  }, 1000);
};

const exportChart = () => {
  console.log('📊 GM Conversation Trend Chart: Exporting chart...');
  emit('export', { chartType: 'conversation-trend', filters: filters.value });
};

const onFiltersChange = (newFilters) => {
  console.log('🔧 GM Conversation Trend Chart: Filters changed:', newFilters);
  filters.value = newFilters;
  emit('filters-change', { chartType: 'conversation-trend', filters: newFilters });
  fetchChartData();
};

// Watch for external filter changes
watch(() => props.filters, (newFilters) => {
  if (newFilters) {
    console.log('🔧 GM Conversation Trend Chart: External filters changed:', newFilters);
    filters.value = { ...filters.value, ...newFilters };
    updateChart();
  }
}, { deep: true });

// Lifecycle
onMounted(async () => {
  console.log('🎯 GM Conversation Trend Chart: Component mounted');
  lastUpdated.value = new Date().toISOString();
  await nextTick();
  updateChart();
});

console.log('✅ GM Conversation Trend Chart: Component setup completed');
</script>

<template>
  <div class="gm-conversation-trend-chart">
    <!-- Chart Controls -->
    <GmChartControls
      :title="title"
      icon="📈"
      :is-loading="isLoading"
      :last-updated="lastUpdated"
      :show-additional-filters="true"
      :channels="channels"
      :agents="agents"
      v-model="filters"
      @refresh="refreshChart"
      @export="exportChart"
      @filters-change="onFiltersChange"
    />
    
    <!-- Chart Stats -->
    <div v-if="chartStats" class="gm-chart-stats">
      <div class="gm-stat-item">
        <span class="gm-stat-label">Total</span>
        <span class="gm-stat-value">{{ chartStats.total }}</span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Avg</span>
        <span class="gm-stat-value">{{ chartStats.average }}</span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Peak</span>
        <span class="gm-stat-value">{{ chartStats.max }}</span>
      </div>
    </div>
    
    <div class="gm-chart-container">
      <div class="gm-chart-placeholder" :style="{ height: height + 'px' }">
        <div v-if="chartData.data.labels.length === 0" class="gm-chart-message">
          <div class="gm-chart-icon-large">📊</div>
          <h3>No Conversation Data Available</h3>
          <p>Conversation data will appear here once available</p>
        </div>
        <div v-else class="gm-chart-canvas-container" :style="{ height: height + 'px' }">
          <canvas ref="chartCanvas"></canvas>
        </div>
      </div>
    </div>
    
    <div v-if="chartStats" class="gm-chart-footer">
      <div class="gm-trend-indicator">
        <span class="gm-trend-icon" :class="{ 'positive': chartStats.trend > 0, 'negative': chartStats.trend < 0 }">
          {{ chartStats.trend > 0 ? '↗️' : chartStats.trend < 0 ? '↘️' : '➡️' }}
        </span>
        <span class="gm-trend-text">
          {{ chartStats.trend > 0 ? '+' : '' }}{{ chartStats.trend }} conversations
          <span class="gm-trend-period">vs previous period</span>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-conversation-trend-chart {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  max-height: 500px;
  overflow: hidden;
}

.gm-conversation-trend-chart:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.gm-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.gm-chart-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.gm-chart-icon {
  font-size: 1.125rem;
}

.gm-chart-stats {
  display: flex;
  gap: 1rem;
}

.gm-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.gm-stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.gm-chart-container {
  margin-bottom: 1rem;
  height: 300px;
  overflow: hidden;
}

.gm-chart-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.gm-chart-placeholder {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.gm-chart-message {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.gm-chart-icon-large {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.gm-chart-message h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.gm-chart-message p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.gm-chart-sample-data {
  display: flex;
  justify-content: space-around;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.gm-sample-point {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-width: 60px;
}

.gm-sample-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-sample-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
}

.gm-chart-footer {
  border-top: 1px solid #f3f4f6;
  padding-top: 1rem;
}

.gm-trend-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-trend-icon {
  font-size: 1rem;
}

.gm-trend-icon.positive {
  color: #10b981;
}

.gm-trend-icon.negative {
  color: #ef4444;
}

.gm-trend-text {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.gm-trend-period {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
  margin-left: 0.25rem;
}

@media (max-width: 768px) {
  .gm-conversation-trend-chart {
    padding: 1rem;
  }
  
  .gm-chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .gm-chart-stats {
    width: 100%;
    justify-content: space-around;
  }
  
  .gm-chart-sample-data {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .gm-sample-point {
    flex-direction: row;
    justify-content: space-between;
    min-width: auto;
  }
}
</style>
